// src/pages/motolivraison/commandes.page.jsx
//
// Liste de toutes les commandes MotoLivraison avec filtres puissants.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Table, message, Card, Row, Col, Tag, Breadcrumb,
  DatePicker, Button,
} from 'antd';
import {
  EyeOutlined, RightOutlined, ContainerOutlined, ClockCircleOutlined,
  CheckCircleOutlined, PhoneOutlined, MobileOutlined,
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { motolivraisonService } from '../../common/services';
import { formatPhoneCI } from '../../common/helper';
import {
  LOCALE_TABLE, PAGINATION_TABLE, BTN_ACTION, COULEURS_ACTION, cmpTexte, CarteStat,
} from './table.helpers';
import './css/motolivraison-table.css';

// Couleurs/labels par statut
const STATUS_CONFIG = {
  en_attente_paiement: { color: 'amber', label: 'Attente paiement' },
  payee: { color: 'cyan', label: 'Payée' },
  en_attente_livreur: { color: 'orange', label: 'Attente livreur' },
  assignee: { color: 'blue', label: 'Assignée' },
  recuperee: { color: 'purple', label: 'Récupérée' },
  en_livraison: { color: 'geekblue', label: 'En livraison' },
  livree: { color: 'lime', label: 'Livrée' },
  confirmee: { color: 'green', label: 'Confirmée' },
  soldee: { color: 'green', label: 'Soldée' },
  annulee: { color: 'red', label: 'Annulée' },
};

const Commandes = () => {
  const [commandes, setCommandes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ dateDebut: null, dateFin: null });
  const navigate = useNavigate();

  const stats = {
    total: commandes.length,
    enAttente: commandes.filter(c => ['en_attente_paiement', 'en_attente_livreur'].includes(c.statut)).length,
    enCours: commandes.filter(c => ['assignee', 'recuperee', 'en_livraison'].includes(c.statut)).length,
    soldees: commandes.filter(c => c.statut === 'soldee').length,
  };

  const fetchCommandes = useCallback(async () => {
    setLoading(true);
    try {
      const data = await motolivraisonService.listCommandes({
        dateDebut: filters.dateDebut ? filters.dateDebut.toISOString() : undefined,
        dateFin: filters.dateFin ? filters.dateFin.toISOString() : undefined,
        // Tri et filtres de colonnes portent sur l'ensemble, pas sur la 1re page serveur
        limit: 1000,
      });
      setCommandes(data?.commandes || []);
    } catch (e) {
      message.error('Erreur lors du chargement des commandes');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchCommandes();
  }, [fetchCommandes]);

  const columns = [
    {
      title: 'Réf', key: 'ref', width: 110,
      render: (_, r) => <code>#{String(r._id).slice(-6).toUpperCase()}</code>,
    },
    {
      title: 'Mode', dataIndex: 'mode', key: 'mode', width: 100, align: 'center',
      filters: [{ text: 'App', value: 'app' }, { text: 'Téléphone', value: 'telephone' }],
      onFilter: (value, r) => r.mode === value,
      render: (m) => (
        <Tag icon={m === 'app' ? <MobileOutlined /> : <PhoneOutlined />} color={m === 'app' ? 'blue' : 'purple'}>
          {m === 'app' ? 'App' : 'Tél'}
        </Tag>
      ),
    },
    {
      title: 'Paiement', dataIndex: 'modePaiement', key: 'paiement', width: 130, align: 'center',
      filters: [
        { text: 'Prépayé', value: 'prepaye' },
        { text: 'Wave à la livraison', value: 'wave_livraison' },
        { text: 'Espèces', value: 'especes' },
      ],
      onFilter: (value, r) => {
        if (value === 'especes') return r.modePaiement === 'especes';
        if (value === 'wave_livraison') {
          return r.modePaiement === 'wave_livraison' || r.mode === 'telephone';
        }
        return r.mode !== 'telephone' && !['wave_livraison', 'especes'].includes(r.modePaiement);
      },
      render: (mp, r) => {
        if (mp === 'especes') return <Tag color="gold">Espèces</Tag>;
        return (r.mode === 'telephone' || mp === 'wave_livraison')
          ? <Tag color="orange">Wave à la livraison</Tag>
          : <Tag color="green">Prépayé</Tag>;
      },
    },
    {
      title: 'Commerce', key: 'commerce', width: 190, ellipsis: true,
      sorter: (a, b) => cmpTexte(a.commerceId?.nom, b.commerceId?.nom),
      render: (_, r) => r.commerceId?.nom || '—',
    },
    {
      title: 'Client', key: 'client', width: 190, ellipsis: true,
      sorter: (a, b) => cmpTexte(
        a.clientNom || `${a.clientId?.firstName || ''} ${a.clientId?.lastName || ''}`,
        b.clientNom || `${b.clientId?.firstName || ''} ${b.clientId?.lastName || ''}`,
      ),
      render: (_, r) => r.clientNom
        || `${r.clientId?.firstName || ''} ${r.clientId?.lastName || ''}`.trim()
        || '—',
    },
    {
      title: 'Téléphone client', key: 'tel', width: 160,
      render: (_, r) => (
        <span style={{ whiteSpace: 'nowrap' }}>{formatPhoneCI(r.clientPhone || r.clientId?.phone)}</span>
      ),
    },
    {
      title: 'Livreur', key: 'livreur', width: 180, ellipsis: true,
      sorter: (a, b) => cmpTexte(
        `${a.livreurId?.firstName || ''} ${a.livreurId?.lastName || ''}`,
        `${b.livreurId?.firstName || ''} ${b.livreurId?.lastName || ''}`,
      ),
      render: (_, r) => r.livreurId
        ? `${r.livreurId.firstName || ''} ${r.livreurId.lastName || ''}`.trim()
        : <span style={{ color: '#bfbfbf' }}>Non assigné</span>,
    },
    {
      title: 'Montant', dataIndex: 'montantTTC', key: 'montant', width: 120, align: 'right',
      sorter: (a, b) => (a.montantTTC || 0) - (b.montantTTC || 0),
      render: (v) => <strong>{Number(v || 0).toLocaleString('fr-FR')} F</strong>,
    },
    {
      title: 'Statut', dataIndex: 'statut', key: 'statut', width: 150,
      filters: Object.entries(STATUS_CONFIG).map(([value, { label }]) => ({ text: label, value })),
      onFilter: (value, r) => r.statut === value,
      render: (s) => {
        const cfg = STATUS_CONFIG[s] || { color: 'default', label: s };
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
    },
    {
      title: 'Créée le', dataIndex: 'createdAt', key: 'createdAt', width: 150,
      defaultSortOrder: 'descend',
      sorter: (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0),
      render: (d) => d ? dayjs(d).format('DD/MM/YYYY HH:mm') : '—',
    },
    {
      title: 'Actions', key: 'action', width: 130, align: 'center', fixed: 'right',
      render: (_, r) => (
        <Button
          size="small"
          icon={<EyeOutlined />}
          style={BTN_ACTION(COULEURS_ACTION.voir)}
          onClick={() => navigate(`/dashboard/motolivraison/commandes/${r._id}`)}
        >
          Détails
        </Button>
      ),
    },
  ];

  return (
    <div className="content-wrapper">
      <div className="container-xxl flex-grow-1" style={{ padding: '8px 24px' }}>
        <Breadcrumb separator={<RightOutlined />} style={{ marginBottom: 12 }}>
          <Breadcrumb.Item><Link to="/dashboard">Accueil</Link></Breadcrumb.Item>
          <Breadcrumb.Item>MotoLivraison</Breadcrumb.Item>
          <Breadcrumb.Item>Commandes</Breadcrumb.Item>
        </Breadcrumb>

        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={12} sm={6}><CarteStat label="Total" value={stats.total} icon={<ContainerOutlined />} /></Col>
          <Col xs={12} sm={6}><CarteStat label="En attente" value={stats.enAttente} icon={<ClockCircleOutlined />} color="#fa8c16" /></Col>
          <Col xs={12} sm={6}><CarteStat label="En cours" value={stats.enCours} icon={<ContainerOutlined />} color="#1890ff" /></Col>
          <Col xs={12} sm={6}><CarteStat label="Soldées" value={stats.soldees} icon={<CheckCircleOutlined />} color="#52c41a" /></Col>
        </Row>

        <Card title="Commandes MotoLivraison">
          <Row gutter={12} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={12}>
              <DatePicker.RangePicker
                value={[filters.dateDebut, filters.dateFin]}
                onChange={(v) => setFilters({ ...filters, dateDebut: v?.[0] || null, dateFin: v?.[1] || null })}
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                placeholder={['Date de début', 'Date de fin']}
                allowEmpty={[true, true]}
              />
            </Col>
          </Row>
          <Table
            className="moto-table"
            columns={columns}
            dataSource={commandes.map(c => ({ ...c, key: c._id }))}
            loading={loading}
            size="middle"
            locale={LOCALE_TABLE}
            pagination={PAGINATION_TABLE('commande')}
            scroll={{ x: 1610 }}
          />
        </Card>
      </div>
    </div>
  );
};

export default Commandes;
